import * as THREE from "three";
import { THREE_GetGifTexture } from "threejs-gif-texture";

const loader = new THREE.TextureLoader();

interface MediaTextureResult {
    texture: THREE.Texture;
    aspect: number;
}

interface GifTexture extends THREE.Texture {
    play: boolean;
    gif: {
        isLoading: boolean;
        hasFailed: boolean;
        width: number;
        height: number;
        totalFrames: number;
    };
}

export function isGifTexture(tex: THREE.Texture): tex is GifTexture {
    return "gif" in tex;
}

const pendingUnmute: Set<HTMLVideoElement> = new Set();
let hasInteracted = false;

function flushPendingUnmutes() {
    hasInteracted = true;
    pendingUnmute.forEach(v => {
        v.muted = false;
        v.play().catch(() => {});
    });
    pendingUnmute.clear();
}

if (typeof window !== "undefined") {
    window.addEventListener("pointerdown", flushPendingUnmutes, { once: true });
    window.addEventListener("keydown", flushPendingUnmutes, { once: true });
}

export function forgetPendingUnmute(video: HTMLVideoElement) {
    pendingUnmute.delete(video);
}

export function createTextureFromWrapperAsync(wrapper: HTMLElement, options?: { autoplay?: boolean }): Promise<MediaTextureResult | null> {
    const autoplay = options?.autoplay ?? true;
    const img = wrapper.querySelector<HTMLImageElement>("img");
    const video = wrapper.querySelector<HTMLVideoElement>("video");

    if (video) {
        return new Promise(resolve => {
            if (video.crossOrigin !== "anonymous") {
                video.crossOrigin = "anonymous";
            }

            if (video.error) {
                resolve(null);
                return;
            }

            const wantsUnmuted = video.muted === false;
            video.muted = true;

            const build = () => {
                if (!video.isConnected) {
                    resolve(null);
                    return;
                }

                const tex = new THREE.VideoTexture(video);
                tex.minFilter = THREE.LinearFilter;
                tex.magFilter = THREE.LinearFilter;

                video.playsInline = true;

                if (autoplay) {
                    video.autoplay = true;
                    video
                        .play()
                        .then(() => {
                            if (wantsUnmuted) {
                                if (hasInteracted) {
                                    video.muted = false;
                                } else {
                                    pendingUnmute.add(video);
                                }
                            }
                        })
                        .catch(() => {});
                }

                resolve({
                    texture: tex,
                    aspect: video.videoWidth / video.videoHeight,
                });
            };

            if (video.readyState >= 1 && video.videoWidth > 0) {
                build();
                return;
            }

            if (video.preload !== "metadata" && video.preload !== "auto") {
                video.preload = "metadata";
                video.load();
            }

            video.addEventListener("loadedmetadata", build, { once: true });
            video.addEventListener("error", () => resolve(null), { once: true });
        });
    }

    if (img && img.src) {
        const isGif = /\.gif(\?|#|$)/i.test(img.src);

        const ensureImageReady = (): Promise<void> => {
            if (img.complete && img.naturalWidth > 0) return Promise.resolve();

            return new Promise(resolve => {
                img.addEventListener("load", () => resolve(), { once: true });
                img.addEventListener("error", () => resolve(), { once: true });
            });
        };

        return ensureImageReady().then(() => {
            if (isGif) {
                return THREE_GetGifTexture(img.src)
                    .then((texture: GifTexture) => {
                        texture.play = true;
                        texture.minFilter = THREE.LinearFilter;
                        texture.magFilter = THREE.LinearFilter;
                        return { texture, aspect: texture.gif.width / texture.gif.height };
                    })
                    .catch((err: unknown) => {
                        console.error("[createTextureFromWrapperAsync] gif failed", img.src, err);
                        return null;
                    });
            }

            return new Promise(resolve => {
                if (img.crossOrigin !== "anonymous") {
                    img.crossOrigin = "anonymous";
                }

                loader.load(
                    img.src,
                    loadedTex => {
                        const { width, height } = loadedTex.image;
                        resolve({ texture: loadedTex, aspect: width / height });
                    },
                    undefined,
                    err => {
                        console.error("[createTextureFromWrapperAsync] failed to load image", img.src, err);
                        resolve(null);
                    }
                );
            });
        });
    }

    return Promise.resolve(null);
}
